import React from "react";
import StarRating from "./StarRating";
import { fmtAvg, fmtDate } from "../format";
import { REVIEWS } from "../../constants/testIds/reviews";

/**
 * ONE published review, as a visitor reads it.
 *
 * PRESENTATIONAL, like `StarRating` and `Pager`: `t` and `lang` are the caller's, so this
 * file binds no dictionary and renders for real in any suite. It is given a `public_review`
 * row (contract §3) and shows exactly what that row carries — never `client_email`, never
 * `hidden`, never a token, because the public row does not have them to show.
 *
 * WHO WROTE IT IS THE SERVER'S ANSWER. `reviewer_display` ("Marie D.") is computed once at
 * submission and stored; this component never assembles a name from parts, so the string a
 * client saw when they submitted is the string every later reader sees, even if the booking
 * is edited afterwards.
 *
 * THE TEXT MAY BE EMPTY — a rating with no words is a complete review (the form's textarea
 * is optional) — and the paragraph is then simply absent rather than an empty box or a
 * placeholder dash, which would read as "they wrote nothing" rather than "they rated only".
 *
 * `text` is rendered as a TEXT NODE. The API stores what the client typed with control
 * characters stripped and nothing else — `<script` included, verbatim — because escaping
 * belongs to the renderer, and React's is the renderer. There is no unsafe raw HTML injection
 * anywhere near this path and there must never be one.
 *
 * `variant` is the SURFACE: the marketplace profile is white-on-midnight, the practitioner's
 * console is ink-on-cream. Gold at 10.26:1 on midnight measures ~1.9:1 on white, so the
 * stars change ink with the panel (see `StarRating`'s own note).
 *
 * NAMESPACE. `t` must be bound to `marketplace` — this component names `rating.ariaStars`
 * and `profile.replyLabel` itself. The practitioner's console does NOT reuse it (it renders
 * `admin/reviews/ReviewRow.jsx`, a different row with moderation controls), so there is no
 * second namespace to satisfy and no reason to pass every string in as a prop.
 *
 * @param {object} review a `public_review` row (or an owner/admin row — the extra keys are ignored)
 * @param {function} t `useT("marketplace")`
 * @param {string} lang the reader's language, for the date
 * @param {"dark"|"light"} [variant]
 */
const TONES = {
  dark: {
    panel: "bg-white/[0.03] border-white/10", name: "text-white", body: "text-white/70",
    muted: "text-white/40", reply: "bg-white/[0.04] border-white/10",
  },
  light: {
    panel: "bg-white border-eh-border-soft", name: "text-eh-ink", body: "text-eh-ink/80",
    muted: "text-eh-muted", reply: "bg-eh-cream/50 border-eh-border-soft",
  },
};

export default function ReviewCard({ review, t, lang, variant = "dark" }) {
  if (!review) return null;
  const tone = TONES[variant] || TONES.dark;
  const reply = review.reply || null;

  return (
    <li
      data-testid={REVIEWS.reviewRow(review.id)}
      className={`list-none rounded-2xl border p-5 ${tone.panel}`}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <StarRating
          value={review.rating}
          readOnly
          variant={variant}
          size="sm"
          label={t("rating.ariaStars", { avg: fmtAvg(review.rating) })}
        />
        <span className={`text-sm font-medium min-w-0 truncate ${tone.name}`}>
          {review.reviewer_display}
        </span>
        <span className={`text-xs tabular-nums ${tone.muted}`}>
          {fmtDate(review.created_at, lang)}
        </span>
        {review.service_name && (
          <span className={`text-xs min-w-0 truncate ${tone.muted}`}>{review.service_name}</span>
        )}
      </div>

      {review.text && (
        <p className={`text-sm mt-3 whitespace-pre-line break-words ${tone.body}`}>{review.text}</p>
      )}

      {reply && (
        <div
          data-testid={REVIEWS.reviewReply(review.id)}
          className={`mt-4 rounded-xl border p-4 ${tone.reply}`}
        >
          <p className={`text-xs uppercase tracking-wider mb-1 ${tone.muted}`}>
            {t("profile.replyLabel")}
          </p>
          <p className={`text-sm whitespace-pre-line break-words ${tone.body}`}>{reply.text}</p>
          <p className={`text-xs mt-1 tabular-nums ${tone.muted}`}>{fmtDate(reply.at, lang)}</p>
        </div>
      )}
    </li>
  );
}
